import React, { useState, useEffect } from 'react';
import { teams as teamsApi } from '../lib/api';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Users, Crown, Mail, Search, UserPlus, Check, X, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export default function TeamsList({ tournament, user, onRefresh }) {
  const [teamsList, setTeamsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const loadTeams = async () => {
    try {
      const res = await teamsApi.list(tournament.id);
      setTeamsList(res.data || []);
    } catch {
      setTeamsList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTeams(); }, [tournament.id]);

  const completeTeams = teamsList.filter(t => t.status === 'complete');
  const lookingTeams = teamsList.filter(t => t.status === 'looking');

  const handleMessageCaptain = async (captainId) => {
    if (!user) {
      toast.error('Connectez-vous pour envoyer un message');
      return;
    }
    try {
      const res = await api.post('/conversations', {
        participant_ids: [captainId],
        tournament_id: null,
      });
      const convId = res.data.id;
      navigate(`/messages?conv=${convId}`);
    } catch {
      toast.error('Erreur lors de la création de la conversation');
    }
  };

  const handleLeaveTeam = async (teamId) => {
    try {
      await teamsApi.leave(tournament.id, teamId);
      toast.success('Vous avez quitté l\'équipe');
      loadTeams();
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleCancelTeam = async (teamId) => {
    if (!window.confirm("Annuler l'inscription de votre équipe ?")) return;
    try {
      await teamsApi.delete(tournament.id, teamId);
      toast.success('Inscription annulée');
      loadTeams();
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const lookingCount = (team) => team.members?.filter(m => m.looking_for_teammate).length || 0;

  const isUserInAnyTeam = user && teamsList.some(
    t => t.captain_id === user.id || t.members?.some(m => m.user_id === user.id)
  );

  if (loading) return null;
  if (teamsList.length === 0) return null;

  return (
    <div className="space-y-4" data-testid="teams-list-section">
      {/* Looking for teammates */}
      {lookingTeams.length > 0 && (
        <Card className="bg-[#1a1a1a] border-[#333]">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-white flex items-center gap-2">
              <Search className="w-4 h-4 text-[#4ECDC4]" />
              Cherchent des coéquipiers ({lookingTeams.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {lookingTeams.map(team => {
              const needed = lookingCount(team);
              const isCaptain = user && team.captain_id === user.id;
              const isMember = user && team.members?.some(m => m.user_id === user.id);

              return (
                <div key={team.id} className="flex items-center justify-between p-3 rounded-xl bg-[#0a0a0a] border border-[#333]" data-testid={`looking-team-${team.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-lg bg-[#4ECDC4]/10 flex items-center justify-center shrink-0">
                      <Users className="w-4 h-4 text-[#4ECDC4]" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-white text-sm font-medium truncate">{team.team_name}</span>
                        <Badge className="bg-[#4ECDC4]/15 text-[#4ECDC4] text-[10px] shrink-0">
                          <UserPlus className="w-2.5 h-2.5 mr-1" />
                          {needed} recherché{needed > 1 ? 's' : ''}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-1 mt-0.5">
                        <Crown className="w-3 h-3 text-[#FF6B35]" />
                        <span className="text-[#888] text-xs">{team.captain_pseudo}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {isCaptain ? (
                      <button onClick={() => handleCancelTeam(team.id)}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 text-xs font-medium hover:bg-red-500/20 transition-colors"
                        data-testid={`cancel-team-${team.id}`}>
                        <Trash2 className="w-3 h-3" /> Annuler
                      </button>
                    ) : isMember ? (
                      <button onClick={() => handleLeaveTeam(team.id)}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 text-xs font-medium hover:bg-red-500/20 transition-colors"
                        data-testid={`leave-team-${team.id}`}>
                        <X className="w-3 h-3" /> Quitter
                      </button>
                    ) : user && !isUserInAnyTeam ? (
                      <button onClick={() => handleMessageCaptain(team.captain_id)}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#FF6B35]/10 text-[#FF6B35] text-xs font-medium hover:bg-[#FF6B35]/20 transition-colors"
                        data-testid={`message-captain-${team.id}`}>
                        <Mail className="w-3.5 h-3.5" /> Message
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Complete teams */}
      {completeTeams.length > 0 && (
        <Card className="bg-[#1a1a1a] border-[#333]">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-white flex items-center gap-2">
              <Check className="w-4 h-4 text-green-400" />
              Équipes inscrites ({completeTeams.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {completeTeams.map(team => {
              const isCaptain = user && team.captain_id === user.id;
              return (
                <div key={team.id} className="flex items-center justify-between p-3 rounded-xl bg-[#0a0a0a] border border-[#333]" data-testid={`complete-team-${team.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-green-500/10 flex items-center justify-center shrink-0">
                      <Users className="w-4 h-4 text-green-400" />
                    </div>
                    <div>
                      <span className="text-white text-sm font-medium">{team.team_name}</span>
                      <div className="flex items-center gap-1 mt-0.5">
                        <Crown className="w-3 h-3 text-[#FF6B35]" />
                        <span className="text-[#888] text-xs">{team.captain_pseudo}</span>
                        {team.members?.filter(m => !m.looking_for_teammate).map((m, i) => (
                          <span key={i} className="text-[#888] text-xs">, {m.pseudo || m.name}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {team.paid ? (
                      <Badge className="bg-green-500/15 text-green-400 text-[10px]"><Check className="w-2.5 h-2.5 mr-1" />Payée</Badge>
                    ) : (
                      <Badge className="bg-yellow-500/15 text-yellow-400 text-[10px]">En attente</Badge>
                    )}
                    {isCaptain && (
                      <button onClick={() => handleCancelTeam(team.id)}
                        className="flex items-center gap-1 px-2 py-1 rounded-lg bg-red-500/10 text-red-400 text-xs hover:bg-red-500/20"
                        data-testid={`cancel-complete-team-${team.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
